import { ColumnDef, sortingFns } from '@tanstack/react-table';
import React from 'react';
import { Link } from 'react-router-dom';
import { getRiskConsequencesName, getRiskEventsName, getRiskSourcesName, getScenarioStatusName } from 'src/api/types';
import { GTInternalIds } from 'src/components/GenericTable/types';
import { formatDate, numeralFormat } from 'src/utils/misc';
import GenericTable, { GTColumnSelectAnchor, GTTdm } from 'src/components/GenericTable';
import { filter } from 'lodash';
import { IncompleteStateMarkerNew, TableSkeleton, TextOverflowTooltip } from 'src/common';
import useLocalStorage from 'src/hooks/useLocalStorage';
import { TableIds } from 'src/api/types/misc';
import { TLLink } from 'src/pages/DashboardPage/comps';
import { useControlRmpQuery } from 'src/graphql/_generated';

interface ControlTableExpandContentProps {
  id: string;
}

const defaultColumns = [
  'status_scen',
  'name',
  'source',
  'event',
  'consequence',
  'expected_loss',
  'updated_at',
  'edited_at',
];
const internalIds = [GTInternalIds.expander, GTInternalIds.tdm];

export const ControlTableExpandContent: React.FC<ControlTableExpandContentProps> = ({ id }) => {
  const [selectedColumns, setSelectedColumns] = useLocalStorage(`${TableIds.rmpScenarios}-columns`, defaultColumns);

  const { data: controlRmpData } = useControlRmpQuery({ variables: { id } });

  const data =
    controlRmpData?.control?.riskManagementPlans.map((el) => ({ ...el, scenario: el.scenario! })).filter(Boolean) || [];

  type Scenario = (typeof data)[number];

  const columns: ColumnDef<Scenario>[] = [
    {
      id: 'assessment_name',
      header: 'Assessment',
      accessorFn: (row) => row.scenario.assessment.name,
      cell: (info) => info.getValue(),
      sortUndefined: 'last',
      sortingFn: sortingFns.text,
    },
    {
      id: 'ux_id',
      header: 'Id',
      accessorFn: (row) => row.scenario.id,
      cell: (info) => info.getValue(),
    },
    {
      id: 'status_scen',
      header: 'Status',
      accessorFn: (row) => getScenarioStatusName(row.scenario.status),
      cell: (info) => info.getValue(),
    },
    {
      id: 'name',
      header: 'Scenario',
      accessorFn: (row) => row.scenario.name,
      cell: (info) => (
        <TextOverflowTooltip>
          <Link
            className="table-link"
            to={`/risk-assessments/${info.row.original.scenario.assessment.id}/scenario/${info.row.original.scenario.id}`}
            onClick={(e) => e.stopPropagation()}
          >
            {info.getValue() as string}
          </Link>
        </TextOverflowTooltip>
      ),
      sortUndefined: 'last',
      sortingFn: sortingFns.text,
    },
    {
      id: 'source',
      header: 'Source',
      accessorFn: (row) => getRiskSourcesName(row.scenario.source),
      cell: (info) => info.getValue(),
    },
    {
      id: 'event',
      header: 'Event',
      accessorFn: (row) => getRiskEventsName(row.scenario.event),
      cell: (info) => info.getValue(),
    },
    {
      id: 'consequence',
      header: 'Consequence',
      accessorFn: (row) => getRiskConsequencesName(row.scenario.consequence),
      cell: (info) => info.getValue(),
    },
    {
      id: 'risk_owner',
      header: 'Risk Owner',
      accessorFn: (row) => row.scenario.ownerName,
      cell: (info) => info.getValue(),
      sortingFn: sortingFns.text,
    },
    {
      id: 'expected_loss',
      header: 'Expected Loss',
      accessorFn: (row) => row.scenario.liveRisk?.expectedLoss,
      cell: (info) => numeralFormat(info.getValue() as number),
    },
    {
      id: 'risk_identified_date',
      header: 'Identified Date',
      accessorFn: (row) => row.scenario.riskIdentifiedDate || '',
      cell: (info) => <TLLink date={info.getValue() as string}>{formatDate(info.getValue() as string)}</TLLink>,
    },
    {
      id: 'updated_at',
      header: 'Last Updated',
      accessorFn: (row) => row.scenario.meta?.updatedAt || null,
      cell: ({ row }) => {
        const date = row.original.scenario.meta?.updatedAt;
        if (!date) return null;
        return <TLLink date={date}>{formatDate(date, { formatType: 'datetime' })}</TLLink>;
      },
    },
    {
      id: 'edited_at',
      header: 'Last Edited Date',
      accessorFn: (row) => row.scenario.editedAt || null,
      cell: ({ row }) => (
        <TLLink date={row.original.scenario.editedAt}>
          {formatDate(row.original.scenario.editedAt, { formatType: 'datetime' })}
        </TLLink>
      ),
    },
    {
      id: 'created_at',
      header: 'Created Date',
      accessorFn: (row) => row.scenario.meta?.createdAt || null,
      cell: ({ row }) => {
        const date = row.original.scenario.meta?.createdAt;
        if (!date) return null;
        return <TLLink date={date}>{formatDate(date, { formatType: 'datetime' })}</TLLink>;
      },
    },
    {
      id: GTInternalIds.tdm,
      header: () => null,
      cell: ({ row }) => {
        return (
          <div css="display: flex;grid-gap: 8px">
            <GTTdm extraComponentLeft={<IncompleteStateMarkerNew scenario={row.original} />} />
          </div>
        );
      },
      size: 1,
    },
  ];

  if (!controlRmpData) return <TableSkeleton />;

  return (
    <GenericTable
      tableId={TableIds.rmpScenarios}
      data={data}
      columns={filter(columns, (el) => [...internalIds, ...selectedColumns].includes(el.id as string))}
      GTColumnSelectAnchorExported={
        <GTColumnSelectAnchor
          tableId={TableIds.rmpScenarios}
          options={[
            { key: 'assessment_name', title: 'Assessment' },
            { key: 'ux_id', title: 'Id' },
            { key: 'status_scen', title: 'Status' },
            { key: 'name', title: 'Scenario' },
            { key: 'source', title: 'Source' },
            { key: 'event', title: 'Event' },
            { key: 'consequence', title: 'Consequence' },
            {
              key: 'risk_owner',
              title: 'Risk Owner',
            },
            {
              key: 'expected_loss',
              title: 'Expected Loss',
            },
            {
              key: 'risk_identified_date',
              title: 'Identified Date',
            },
            {
              key: 'updated_at',
              title: 'Last Updated',
            },
            {
              key: 'edited_at',
              title: 'Last Edited Date',
            },
            {
              key: 'created_at',
              title: 'Created Date',
            },
          ]}
          defaultOptions={defaultColumns}
          value={selectedColumns}
          onChange={setSelectedColumns}
        />
      }
      darkMode
    />
  );
};
